import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ListParams } from '@/types/api'
import type {
  BookDetail,
  BookFilters,
  Book,
  Borrower,
  BorrowerDetail,
  BorrowerKind,
  Category,
  FamilyLibraryData,
  LibrarySettings,
  LibraryStats,
  Loan,
  LoanFilters,
  StaffLibraryData,
} from './types'

export const libraryKeys = {
  all: ['library'] as const,
  books: (params: object) => [...libraryKeys.all, 'books', params] as const,
  book: (id: number) => [...libraryKeys.all, 'book', id] as const,
  languages: () => [...libraryKeys.all, 'languages'] as const,
  categories: () => [...libraryKeys.all, 'categories'] as const,
  loans: (params: object) => [...libraryKeys.all, 'loans', params] as const,
  borrowers: (search: string, kind?: BorrowerKind) =>
    [...libraryKeys.all, 'borrowers', search, kind ?? 'all'] as const,
  borrower: (kind: BorrowerKind, id: number) => [...libraryKeys.all, 'borrower', kind, id] as const,
  settings: () => [...libraryKeys.all, 'settings'] as const,
  stats: () => [...libraryKeys.all, 'stats'] as const,
  myLoans: () => [...libraryKeys.all, 'me', 'staff'] as const,
  childLoans: (studentId: number) => [...libraryKeys.all, 'me', 'family', studentId] as const,
}

export const booksQuery = (params: ListParams & BookFilters) =>
  queryOptions({
    queryKey: libraryKeys.books(params),
    queryFn: () => api.page<Book>('/library/books', params),
    placeholderData: keepPreviousData,
  })
export const bookQuery = (id: number) =>
  queryOptions({ queryKey: libraryKeys.book(id), queryFn: () => api.get<BookDetail>(`/library/books/${id}`) })
export const languagesQuery = () =>
  queryOptions({
    queryKey: libraryKeys.languages(),
    queryFn: () => api.get<string[]>('/library/books/languages'),
    staleTime: 300_000,
  })
export const categoriesQuery = () =>
  queryOptions({
    queryKey: libraryKeys.categories(),
    queryFn: () => api.get<Category[]>('/library/categories'),
    staleTime: 60_000,
  })
export const loansQuery = (params: ListParams & LoanFilters) =>
  queryOptions({
    queryKey: libraryKeys.loans(params),
    queryFn: () => api.page<Loan>('/library/loans', params),
    placeholderData: keepPreviousData,
  })
export const borrowersQuery = (search: string, kind?: BorrowerKind) =>
  queryOptions({
    queryKey: libraryKeys.borrowers(search, kind),
    queryFn: () => api.get<Borrower[]>('/library/borrowers', { params: { search, kind, limit: 8 } }),
    enabled: search.trim().length > 0,
    placeholderData: keepPreviousData,
  })
export const borrowerQuery = (kind: BorrowerKind, id: number) =>
  queryOptions({
    queryKey: libraryKeys.borrower(kind, id),
    queryFn: () => api.get<BorrowerDetail>(`/library/borrowers/${kind}/${id}`),
  })
export const librarySettingsQuery = () =>
  queryOptions({
    queryKey: libraryKeys.settings(),
    queryFn: () => api.get<LibrarySettings>('/library/settings'),
  })
export const libraryStatsQuery = () =>
  queryOptions({ queryKey: libraryKeys.stats(), queryFn: () => api.get<LibraryStats>('/library/stats') })
export const myLibraryQuery = () =>
  queryOptions({
    queryKey: libraryKeys.myLoans(),
    queryFn: () => api.get<StaffLibraryData>('/me/staff/library'),
  })
export const childLibraryQuery = (studentId: number) =>
  queryOptions({
    queryKey: libraryKeys.childLoans(studentId),
    queryFn: () => api.get<FamilyLibraryData>(`/me/family/students/${studentId}/library`),
  })

export interface BookPayload {
  title: string
  subtitle: string | null
  authors: string
  isbn: string | null
  publisher: string | null
  publishedYear: number | null
  categoryId: number | null
  language: string | null
  shelfLocation: string | null
  totalCopies: number
  description: string | null
  active: boolean
  coverFileId: number | null
  removeCover: boolean
}

/** Every library write touches copies, loans or stats, so the whole area is refreshed. */
function useLibraryMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: libraryKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export const useSaveBook = () =>
  useLibraryMutation(({ id, ...payload }: BookPayload & { id?: number }) =>
    id
      ? api.put<BookDetail>(`/library/books/${id}`, payload)
      : api.post<BookDetail>('/library/books', payload),
  )
export const useDeleteBook = () => useLibraryMutation((id: number) => api.delete(`/library/books/${id}`))
export const useSaveCategory = () =>
  useLibraryMutation(({ id, name }: { id?: number; name: string }) =>
    id
      ? api.put<Category>(`/library/categories/${id}`, { name })
      : api.post<Category>('/library/categories', { name }),
  )
export const useDeleteCategory = () =>
  useLibraryMutation((id: number) => api.delete(`/library/categories/${id}`))
export const useSaveSettings = () =>
  useLibraryMutation((body: Omit<LibrarySettings, 'currency' | 'finePerDay'> & { finePerDay: number }) =>
    api.put<LibrarySettings>('/library/settings', body),
  )

export const useIssueLoan = () =>
  useLibraryMutation(
    (body: {
      bookId: number
      borrowerKind: BorrowerKind
      borrowerId: number
      dueOn?: string | null
      notes?: string | null
    }) => api.post<Loan>('/library/loans', body),
  )
export const useReturnLoan = () =>
  useLibraryMutation((id: number) => api.post<Loan>(`/library/loans/${id}/return`))
export const useRenewLoan = () =>
  useLibraryMutation((id: number) => api.post<Loan>(`/library/loans/${id}/renew`))
export const usePayFine = () =>
  useLibraryMutation((id: number) => api.post<Loan>(`/library/loans/${id}/pay-fine`))
